import {
  CollectionName,
  WebsiteCustomerMessageRequestBody,
  CustomerChannel,
  CloudFunction,
  DialogflowQueryResult,
  FirebaseSite,
  ConversationCheckAvailability,
  Conversation,
} from '@tomis-tech/types';
import firebase from 'firebase/app';

import { createCustomerUserMessage, createErrorMessage } from '../messages';
import { DEFAULT_CHECK_AVAILABILITY_PRODUCTS, SITE_SLUG } from '../consts';
import { invokeFunction } from '../../cloud-functions';
import { logError, Category } from '.././sentry-iframe';
import { FIREBASE, getFirebaseUserId } from '../../firebase';
import { PARENT_PAGE_VIEW } from '..';

/** Send a customer message to ChatBot:
 * 1. Add Message directly to Firebase Conversation
 * 2. Invoke `newCustomerMessage` cloud function
 * 3. Toggle `conversationMode` if Dialogflow action === `"show_lead_form"` and
 * bot is configured for SMS Chat
 *
 * @returns Will ALWAYS resolve. No need for try/catch
 */
export async function sendNewCustomerMessage({
  text,
  conversationId,
}: {
  /** The customer message (always plain-text) */
  text: string;
  /** ChatBot conversation id `Sites/<site-slug>/Conversations/` */
  conversationId: string;
}): Promise<{ success: boolean; intentAction?: string }> {
  /** Regular message to bot or assigned agent */
  const message = createCustomerUserMessage({
    text,
    uid: getFirebaseUserId(),
  });

  const body: WebsiteCustomerMessageRequestBody = {
    customerChannel: CustomerChannel.WebsiteChatbot,
    siteSlug: SITE_SLUG,
    messageId: message.id,
    text: message.text,
    sent: message.sent,
    parentHostname: PARENT_PAGE_VIEW.page.url.hostname,
    conversationId,
  };

  const conversationRef = FIREBASE.db
    .collection(CollectionName.Sites)
    .doc(SITE_SLUG)
    .collection(CollectionName.Conversations)
    .doc(conversationId);

  try {
    await conversationRef
      .collection(CollectionName.Messages)
      .doc(message.id)
      .set(message, { merge: true });
  } catch (error) {
    logError(error);
    return { success: false };
  }

  return invokeFunction({
    name: CloudFunction.NewCustomerMessage,
    body,
    logToSentry: true,
  })
    .then(
      async ([responseBody, response]: [
        {
          useHumanAgent: boolean;
          dialogflowQueryResult: DialogflowQueryResult;
          originalDialogflowQueryResult: DialogflowQueryResult;
          error?: string;
        },
        Response
      ]) => {
        if (response.ok) {
          if (responseBody.originalDialogflowQueryResult) {
            const intentAction =
              responseBody.originalDialogflowQueryResult?.action;

            if (intentAction === 'show_lead_form') {
              await handleShowLeadForm(conversationRef);
            } else if (intentAction === 'check_availability') {
              await handleCheckAvailabilityMessage(conversationRef);
            }

            return { success: true, intentAction };
          } else {
            console.warn(
              "Response didn't include originalDialogflowQueryResult"
            );
            // Response was OK, return true
            return { success: true };
          }
        } else {
          // Response was NOT ok, return false
          // Error was already logged to sentry
          return { success: false };
        }
      }
    )
    .catch(error => {
      const errorMessage = createErrorMessage(
        'error sending message, try again'
      );

      const category = Category[CloudFunction.NewCustomerMessage];

      logError(error, {
        category,
        message: `Error requesting newCustomer Message`,
        level: 'error',
        type: 'error',
        data: error,
      });

      conversationRef
        .collection(CollectionName.Messages)
        .add(errorMessage)
        .catch(logError);

      return { success: false };
    });
}

/** If Dialogflow message is to show lead form, we need to toggle
 * `conversationMode` on the Firebase conversation, which will
 * trigger the web-client to display the SMS Chat form.
 *
 * @returns void; Error is logged. No additional action needed */
async function handleShowLeadForm(
  conversationRef: firebase.firestore.DocumentReference
) {
  try {
    const site = (
      await conversationRef.parent.parent.get()
    ).data() as FirebaseSite;
    const isSmsEnabled = site.features.includes('liveChatSMS');

    if (isSmsEnabled) {
      conversationRef
        .update({ conversationMode: 'liveChatSMS' })
        .catch(logError);
    }
  } catch (error) {
    logError(error, {
      message: 'Unable to toggle conversation mode',
      category: Category.newCustomerMessage,
    });
  }
}

/** If Dialogflow message is check availability, we need to setup
 * Firebase to handle storing quantity, tour, and date range.
 *
 * @returns void; Error is logged. No additional action needed */
async function handleCheckAvailabilityMessage(
  conversationRef: firebase.firestore.DocumentReference
) {
  /** Check Availability values to store in Firebase */
  const defaultValues: ConversationCheckAvailability = {
    active: true,
    quantity: 1, // default to 1
    dateTimeStart: null,
    dateTimeEnd: null,
    products: DEFAULT_CHECK_AVAILABILITY_PRODUCTS,
    searches: [],
  };
  try {
    const conversation: Conversation = (
      await conversationRef.get()
    ).data() as Conversation;

    const firstTime = !conversation.checkAvailability;
    const data = conversation.checkAvailability || defaultValues;

    const site = (
      await conversationRef.parent.parent.get()
    ).data() as FirebaseSite;

    if (site.botConfig.checkAvailability?.fixedQuantity) {
      // Sites with fixed quantity will always use the same quantity
      data.quantity = site.botConfig.checkAvailability.fixedQuantity;
    }

    if (data.active === false || firstTime) {
      data.active = true;
      await conversationRef.update({ checkAvailability: data });
    }
  } catch (error) {
    logError(error, {
      message: 'Unable to toggle Check Availability',
      category: Category.CheckAvailability,
    });
  }
}
